import { createContext, useContext, useState, useEffect } from 'react'

const CookieConsentContext = createContext(null)

const COOKIE_CONSENT_KEY = 'synthetiq_cookie_consent'
const COOKIE_PREFERENCES_KEY = 'synthetiq_cookie_preferences'

const defaultPreferences = {
  essential: true, // Always required, cannot be disabled
  functional: false,
  analytics: false,
  marketing: false,
}

export function CookieConsentProvider({ children }) {
  const [consentGiven, setConsentGiven] = useState(null) // null = not decided, true = accepted, false = declined
  const [preferences, setPreferences] = useState(defaultPreferences)
  const [showBanner, setShowBanner] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)

  // Load saved consent on mount
  useEffect(() => {
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY)
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY)

    if (savedConsent !== null) {
      setConsentGiven(savedConsent === 'true')
      if (savedPreferences) {
        try {
          setPreferences(JSON.parse(savedPreferences))
        } catch (e) {
          setPreferences(defaultPreferences)
        }
      }
      setShowBanner(false)
    } else {
      // No consent recorded, show banner after a brief delay
      const timer = setTimeout(() => setShowBanner(true), 1000)
      return () => clearTimeout(timer)
    }
  }, [])

  const acceptAll = () => {
    const allAccepted = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    }
    setPreferences(allAccepted)
    setConsentGiven(true)
    setShowBanner(false)
    setShowPreferences(false)
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true')
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(allAccepted))
    
    // Dispatch event for analytics/tracking initialization
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: allAccepted }))
  }

  const acceptEssentialOnly = () => {
    const essentialOnly = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
    }
    setPreferences(essentialOnly)
    setConsentGiven(true)
    setShowBanner(false)
    setShowPreferences(false)
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true')
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(essentialOnly))
    
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: essentialOnly }))
  }

  const savePreferences = (newPreferences) => {
    const finalPreferences = {
      ...newPreferences,
      essential: true, // Always keep essential enabled
    }
    setPreferences(finalPreferences)
    setConsentGiven(true)
    setShowBanner(false)
    setShowPreferences(false)
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true')
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(finalPreferences))
    
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: finalPreferences }))
  }

  const openPreferences = () => {
    setShowPreferences(true)
  }

  const closePreferences = () => {
    setShowPreferences(false)
  }

  const resetConsent = () => {
    localStorage.removeItem(COOKIE_CONSENT_KEY)
    localStorage.removeItem(COOKIE_PREFERENCES_KEY)
    setConsentGiven(null)
    setPreferences(defaultPreferences)
    setShowBanner(true)
  }

  const value = {
    consentGiven,
    preferences,
    showBanner,
    showPreferences,
    acceptAll,
    acceptEssentialOnly,
    savePreferences,
    openPreferences,
    closePreferences,
    resetConsent,
  }

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  )
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext)
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider')
  }
  return context
}